import { isSelectOptionDisabled } from "./isSelectOptionDisabled";

/**
 * Normalizes a label/value for tolerant matching: trims surrounding whitespace,
 * collapses internal runs of whitespace, and lower-cases. Used as a fallback when
 * an exact key lookup misses, so Operator picking "elderberry " or "Elderberry"
 * still resolves to the "Elderberry" option it saw in the lookup results.
 */
const normalizeKey = (key: string): string => key.trim().replace(/\s+/g, " ").toLowerCase();

/**
 * Finds an option in `map` by `key`, first by exact match and then by a tolerant
 * (trimmed, whitespace-collapsed, case-insensitive) match. Returns undefined when
 * nothing matches. The tolerant pass only kicks in on an exact miss, so the common
 * case stays a direct object lookup.
 */
const findOption = <OptionType>(map: Record<string, OptionType>, key: string): OptionType | undefined => {
  const exact = map[key];
  if (exact !== undefined) {
    return exact;
  }

  const normalizedTarget = normalizeKey(key);
  const matchKey = Object.keys(map).find((candidate) => normalizeKey(candidate) === normalizedTarget);
  return matchKey === undefined ? undefined : map[matchKey];
};

/**
 * Resolves an option for the Operator `select_option` tool callback.
 *
 * When `hasSearchFn` is true, the resolver matches against the search-result cache
 * first and then falls back to the currently-rendered options (`optionsMap`). For
 * selects with many options (paginated, async, or backend-filtered) Operator cannot
 * assume the rendered options are exhaustive, so options beyond the initial set must
 * be discovered via `lookup_options` — but options already rendered are
 * known-valid and selectable directly. On a miss the error still directs Operator to
 * `lookup_options`, since the missing option is, by definition, not yet rendered.
 *
 * When `hasSearchFn` is false, the resolver matches the currently-rendered options
 * map first and then the cache.
 *
 * Matching is tolerant of minor label discrepancies (surrounding/duplicated
 * whitespace, letter case) via {@link findOption}, because the option label is
 * round-tripped through the LLM between `lookup_options` and `select_option` and
 * isn't always reproduced byte-for-byte. This removes a common intermittent
 * "Option not found" failure.
 */
export const resolveOptionForSelect = <OptionType>({
  optionValue,
  optionsMap,
  cachedOptions,
  hasSearchFn,
}: {
  optionValue: string;
  optionsMap: Record<string, OptionType>;
  cachedOptions: Record<string, OptionType>;
  hasSearchFn: boolean;
}): OptionType => {
  if (hasSearchFn) {
    const option = findOption(cachedOptions, optionValue) ?? findOption(optionsMap, optionValue);
    if (option === undefined) {
      throw new Error(
        `Option "${optionValue}" not found. Use the lookup_options tool first to search for available options.`
      );
    }
    assertOptionIsEnabled(option, optionValue);
    return option;
  }

  const option = findOption(optionsMap, optionValue) ?? findOption(cachedOptions, optionValue);
  if (option === undefined) {
    throw new Error(`Option with value ${optionValue} not found`);
  }
  assertOptionIsEnabled(option, optionValue);
  return option;
};

/**
 * Rejects disabled react-select options so Operator cannot pick a value a person cannot.
 *
 * @param option - The resolved dropdown option.
 * @param optionValue - The label/value Operator passed to the select tool.
 */
const assertOptionIsEnabled = <OptionType>(option: OptionType, optionValue: string): void => {
  if (isSelectOptionDisabled(option)) {
    throw new Error(`Option "${optionValue}" is disabled and cannot be selected.`);
  }
};
